/**
 * The rule that makes a hostname a workspace's address: one valid,
 * unreserved label under the platform's base domain. Kept free of the
 * app's own configuration so the Vite config can use it too — it sends a
 * company address's website paths to the site server (P19). Mirrors
 * TenantHost in the API; keep the two in step.
 */

const LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/

/** Never a workspace, whatever a tenant might ask for (see tenancy.reserved_subdomains). */
const RESERVED = new Set([
  'www', 'admin', 'api', 'app', 'auth', 'login', 'account', 'accounts',
  'mail', 'static', 'assets', 'cdn', 'status', 'help', 'support', 'docs',
  // The platform's own infrastructure labels (P12).
  'sites', 'origin', 'customers', 'domains', 'dns', 'ns1', 'ns2', 'smtp',
  'email', 'webmail', 'autoconfig', 'autodiscover', 'ftp', 'staging', 'sandbox',
  'dev', 'test', 'portal', 'pay', 'payments', 'billing',
  // `demo` left the list on 2026-10-11: the platform's demonstration company lives at demo.<base>.
])

/** The workspace slug a hostname (no port) names under `baseDomain`, or null when it names none. */
export function slugOf(hostname: string, baseDomain: string): string | null {
  const host = hostname.toLowerCase().replace(/\.$/, '')
  const suffix = `.${baseDomain}`

  if (baseDomain === '' || !host.endsWith(suffix)) return null

  const label = host.slice(0, -suffix.length)

  return LABEL.test(label) && !RESERVED.has(label) ? label : null
}
